import appConfig from '../../config/appConfig';
import * as iccMethods from '../../consts/icc/iccMethods';
import {
    setConsumerAcceptedTnc,
    setConsumerAggregatedSettings,
    setConsumerData,
    setConsumerProducts,
    setConsumerTnc,
} from '../../state/ducks/consumer';
import {dispatch} from '../../state/store';
import arrayUtils from '../../utils/arrayUtils';
import urlUtils from '../../utils/urlUtils';
import appInstanceService from '../app/appInstanceService';
import authDataService from '../auth/authDataService';
import languageService from '../localization/languageService';
import log from '../logger/log';
import {
    aggregatedSettingsMapping,
    consumerMapping,
    consumerProductsMapping,
    deviceRegistrationMapping,
    externalConsumerMapping,
    mediaUrlsMapping,
    tncMapping,
} from '../mapping/iccMarketMappings';
import server from '../server/server';
import iccResponseErrorCheckService from './iccResponseErrorCheckService';

const getIccMarketApiUrl = (methodName) => urlUtils.join(appConfig.getIccApiUrl(), methodName);

const getAnonymousAggregatedSettings = (throwException) =>
    callGet({
        methodName: iccMethods.GET_ANONYMOUS_AGGREGATED_SETTINGS,
        mapper: aggregatedSettingsMapping,
        action: setConsumerAggregatedSettings,
        params: {SessionID: appInstanceService.getAppInstanceId()},
        throwException,
    });

const getConsumerProfile = () =>
    callGet({
        methodName: iccMethods.GET_CONSUMER,
        mapper: consumerMapping,
        action: setConsumerData,
        throwException: true,
    });

const getExternalConsumerProfile = () =>
    callGet({
        methodName: iccMethods.GET_EXTERNAL_CONSUMER,
        mapper: externalConsumerMapping,
        action: setConsumerData,
        throwException: true,
    });

const getConsumerProducts = () =>
    callGet({
        methodName: iccMethods.CONSUMER_PRODUCTS,
        mapper: consumerProductsMapping,
        action: setConsumerProducts,
    });

const getMediaUrl = ({mediaNameList, throwException = false, isV1 = false}) => {
    if (mediaNameList) {
        return callPost({
            methodName: isV1 ? iccMethods.GET_MEDIA_URL : iccMethods.GET_MEDIA_URL_V2,
            requestConfig: {data: {mediaName: arrayUtils.toArray(mediaNameList)}},
            mapper: mediaUrlsMapping,
            throwException,
        });
    }
};

const getAnonymousTnc = () =>
    callGet({
        methodName: iccMethods.GET_ANONYMOUS_TNC,
        mapper: tncMapping,
        action: setConsumerTnc,
        throwException: true,
        params: {SessionID: appInstanceService.getAppInstanceId()},
    });

const setAnonymousTnc = ({data}) =>
    callPost({
        methodName: iccMethods.SET_ANONYMOUS_TNC,
        requestConfig: {data},
        throwException: true,
    });

const declineTnc = ({data}) =>
    callDelete({
        methodName: iccMethods.SET_CONSUMER_TNC,
        requestConfig: {data},
        throwException: true,
    });

const getAnonymousAcceptedTnc = () =>
    callGet({
        methodName: iccMethods.GET_ANONYMOUS_ACCEPTED_TNC,
        mapper: tncMapping,
        action: setConsumerAcceptedTnc,
        params: {
            includeDeclined: true,
            SessionID: appInstanceService.getAppInstanceId(),
        },
        throwException: true,
    });

const setConsumerProductV2 = ({codentify, purchasedOn}) =>
    callPost({
        methodName: iccMethods.CONSUMER_PRODUCTS_V2,
        requestConfig: {params: {codentifyId: codentify, purchasedOn}},
        mapper: deviceRegistrationMapping,
        throwException: true,
    });

const setConsumerProductV3 = ({deviceSerialNumber, purchasedOn}) =>
    callPost({
        methodName: iccMethods.CONSUMER_PRODUCTS_V3,
        requestConfig: {params: {deviceSerialNumber, purchasedOn}},
        mapper: deviceRegistrationMapping,
        throwException: true,
    });

const callGet = ({methodName, mapper, action, params = {}, throwException}) => {
    return callRequest({
        httpMethod: server.get,
        methodName,
        requestConfig: {params},
        mapper,
        action,
        throwException,
    });
};

const callPost = ({methodName, requestConfig, mapper, throwException}) => {
    return callRequest({
        httpMethod: server.post,
        methodName,
        requestConfig,
        mapper,
        throwException,
    });
};

const callDelete = ({methodName, requestConfig, mapper, throwException}) => {
    return callRequest({
        httpMethod: server.requestDelete,
        methodName,
        requestConfig,
        mapper,
        throwException,
    });
};

const callRequest = async ({httpMethod, methodName, requestConfig, mapper, action, throwException = false}) => {
    try {
        const response = await callIccMethod(httpMethod, methodName, requestConfig);

        const {data} = response;

        if (mapper) {
            const mappedData = mapper(data);

            if (action) {
                dispatch(action(mappedData));
            }

            return mappedData;
        } else {
            return httpMethod === server.get ? data : response;
        }
    } catch (e) {
        if (throwException) {
            return Promise.reject(e);
        }
    }
};

const callIccMethod = async (httpMethod, methodName, requestConfig = {}) => {
    const methodUrl = getIccMarketApiUrl(methodName);

    try {
        const config = await getIccRequestConfig(requestConfig);
        const response = await httpMethod(methodUrl, config);

        log.debug(`iccMarketService: request: '${methodName}' has successful response`);

        return response;
    } catch (e) {
        const isHandleError = methodName !== iccMethods.GET_PRODUCT_INFO_LIST;

        await iccResponseErrorCheckService(e, isHandleError);

        const config = await getIccRequestConfig(requestConfig);

        return await httpMethod(methodUrl, config);
    }
};

const getIccRequestConfig = async (requestConfig) => {
    const authToken = await authDataService.getAuthToken();
    const appVersion = appConfig.getAppVersion();
    const headers = {Authorization: 'Bearer ' + authToken, v: appVersion};
    const languageCode = languageService.getCurrentLanguageCode()?.toUpperCase();

    if (languageCode) {
        headers.l = languageCode;
    }

    return {
        headers,
        ...requestConfig,
    };
};

export default {
    declineTnc,
    getAnonymousTnc,
    getAnonymousAcceptedTnc,
    getAnonymousAggregatedSettings,
    getConsumerProducts,
    getConsumerProfile,
    getExternalConsumerProfile,
    getIccMarketApiUrl,
    getMediaUrl,
    setAnonymousTnc,
    setConsumerProductV2,
    setConsumerProductV3,
};
